import InfoCardVertical from "../../components/InfoCardVertical.js";
import { AreaChartComponent } from "../../components/AreaChartComponent.tsx";
import { Leaderboard } from "../../components/Leaderboard.tsx";
import { ChevronLeft, ChevronRight, LucideRecycle } from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import useUserStore2 from "../../stores/userStore2.js";
import { dashboardService } from "../../services/dashboardService";
import { Skeleton } from "../../components/ui/skeleton";
import MemberShipCard from "../../components/MemberShipCard.tsx";

export default function MemDashboard() {
  const { user, currentProfile } = useUserStore2();
  const userId = user?.id; // Access user ID from user object
  const profileId = user?.currentProfile?.id; // Access profile ID from currentProfile (changed from profileId to id)
  const [pointsData, setPointsData] = useState({
    total_points: 0,
    total_points_old: 0,
  });
  const [graphData, setGraphData] = useState([]);
  const [loading, setLoading] = useState({
    points: true,
    graph: true,
  });
  const [error, setError] = useState({
    points: null,
    graph: null,
  });

  useEffect(() => {
    // console.log("Current user store values:", {
    //   userId,
    //   profileId,
    //   user: user,
    //   currentProfile: currentProfile,
    // });

    const fetchUserPoints = async () => {
      try {
        setLoading((prev) => ({ ...prev, points: true }));
        const response = await dashboardService.countProfilePoints(
          userId,
          profileId
        );
        // console.log("Points API Response:", response);

        if (
          response &&
          typeof response === "object" &&
          "total_points" in response &&
          "total_points_old" in response
        ) {
          setPointsData(response);
        } else {
          throw new Error("Invalid points data format");
        }
      } catch (err) {
        console.error("Failed to fetch profile points:", err);
        setError((prev) => ({ ...prev, points: "Failed to load points data" }));
      } finally {
        setLoading((prev) => ({ ...prev, points: false }));
      }
    };

    const fetchProfileContributions = async () => {
      try {
        if (!profileId) {
          throw new Error("Profile ID is not available");
        }

        setLoading((prev) => ({ ...prev, graph: true }));
        // Set dates from January 1st to December 1st of current year
        const currentYear = new Date().getFullYear();
        const today = new Date();
        const startDate = `${currentYear}-03-01`; // March 1st of current year
        const endDate = today.toISOString().split("T")[0];

        // console.log("Making API call with:", {
        //   userId,
        //   profileId,
        //   startDate,
        //   endDate,
        // });

        const response = await dashboardService.countProfileContributions(
          userId,
          profileId,
          startDate,
          endDate
        );

        // console.log("Graph API FULL Response:", response);

        if (response && response.Data && Array.isArray(response.Data)) {
          const transformedData = response.Data.map((item) => ({
            date: item._id,
            total_quantities: item.total_points,
            count: item.count,
          }));
          setGraphData(transformedData);
        } else {
          throw new Error("Invalid graph data format");
        }
      } catch (err) {
        console.error("Failed to fetch profile contributions:", err);
        setError((prev) => ({ ...prev, graph: "Failed to load graph data" }));
      } finally {
        setLoading((prev) => ({ ...prev, graph: false }));
      }
    };

    if (userId) {
      fetchUserPoints();
      if (profileId) {
        fetchProfileContributions();
      } else {
        console.warn("Profile ID is not available");
        setLoading((prev) => ({ ...prev, graph: false }));
        setError((prev) => ({ ...prev, graph: "Profile not selected" }));
      }
    }
  }, [userId, profileId, user, currentProfile]); // Added user and currentProfile to dependencies

  const percentageChange = pointsData.total_points_old
    ? (
        ((pointsData.total_points - pointsData.total_points_old) /
          pointsData.total_points_old) *
        100
      ).toFixed(1)
    : "0.0";

  const scrollRef = useRef(null);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -250, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 250, behavior: "smooth" });
    }
  };

  const data = user?.currentProfile?.memberships || [];
  const showLoading = false;

  return (
    <div className="flex flex-col md:flex-row min-h-screen ">
      <div className="flex-1 flex flex-col">
        <div className="p-4 md:p-8 flex flex-col">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="h-full">
              {loading.points ? (
                <div className="bg-white p-6 rounded-lg shadow h-full">
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-8 w-full mb-4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ) : (
                <InfoCardVertical
                  className="h-full"
                  heading="Total Recycled Points"
                  number={
                    error.points
                      ? "N/A"
                      : pointsData.total_points.toLocaleString(undefined, {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })
                  }
                  icon={LucideRecycle}
                  bottomText={error.points || "Updated Today"}
                  bgColor={error.points ? "bg-red-100" : "bg-fuchsia-200"}
                  iconColor={error.points ? "text-red-600" : "text-fuchsia-600"}
                  trend={{
                    percentage: percentageChange + "%",
                    isUp: parseFloat(percentageChange) >= 0,
                  }}
                />
              )}
            </div>
            <div className="md:col-span-3 h-full">
              {loading.graph ? (
                <div className="bg-white p-6 rounded-lg shadow h-full">
                  <Skeleton className="h-full w-full" />
                </div>
              ) : error.graph ? (
                <div className="bg-white p-6 rounded-lg shadow h-full flex items-center justify-center">
                  <p className="text-red-500">{error.graph}</p>
                </div>
              ) : (
                <AreaChartComponent
                  className="h-full"
                  data={graphData}
                  dataKey="value"
                  xAxisKey="date"
                  title="Recycled Points"
                  description="Total points recycled in the last 90 days"
                  chartLabel="Recycled Points"
                />
              )}
            </div>
          </div>
        </div>
        <div className="flex-1 p-4 md:p-8 pt-0 sm:pt-0 md:pt-0">
          <div
            id="whitedivbox"
            className="bg-white shadow container rounded-lg md:max-w-[calc(100vw-375px)] p-4 sm:p-6 text-sm md:mt-0 relative overflow-hidden w-full"
          >
            {/* Title */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
              <div className="text-base sm:text-lg font-semibold text-[#111827]">
                Community Memberships
              </div>
            </div>
            <hr />

            {/* Scrollable container */}
            <div className="flex items-center w-full overflow-x-auto md:overflow-visible">
              {/* Left Arrow */}
              <button
                onClick={scrollLeft}
                className="hidden md:inline-flex bg-white border border-gray-300 p-2 mr-2 rounded-full hover:bg-gray-100"
              >
                <ChevronLeft className="w-5 h-5 text-gray-700" />
              </button>

              {/* Scroll container */}
              <div className="overflow-x-auto w-full">
                <div
                  ref={scrollRef}
                  className="flex gap-4 sm:gap-6 overflow-x-auto no-scrollbar scroll-smooth whitespace-nowrap w-full px-1"
                >
                  {/*{getMembershipDetails.loading*/}
                  {showLoading ? (
                    [...Array(3)].map((_, index) => (
                      <Skeleton
                        key={index}
                        className="min-w-[16rem] w-64 h-40 rounded-lg"
                      />
                    ))
                  ) : // : Array.isArray(getMembershipDetails.data) && getMembershipDetails.data.length === 0 ? (
                  data.length === 0 ? (
                    <div
                      className="min-w-[16rem] w-64 h-40 rounded-lg flex items-center justify-center border-2 border-dotted border-gray-300 bg-gray-50 text-gray-400"
                      style={{ borderStyle: "dotted" }}
                    >
                      No membership found
                    </div>
                  ) : (
                    // : getMembershipDetails.data?.map((membership) => (
                    user?.currentProfile?.memberships?.map((membership) => (
                      <MemberShipCard
                        key={membership.membershipId}
                        name={membership.communityName}
                        groupName={membership.groupName || ""}
                        variant="teal"
                        communityId={membership.communityId}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Right Arrow */}
              <button
                onClick={scrollRight}
                className="hidden md:inline-flex bg-white border border-gray-300 p-2 ml-2 rounded-full hover:bg-gray-100"
              >
                <ChevronRight className="w-5 h-5 text-gray-700" />
              </button>
            </div>
          </div>
        </div>
        <Leaderboard title={"Community Leaderboard"} />
      </div>
    </div>
  );
}
